import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectSeparator, SelectTrigger, SelectValue } from "../ui/select";
import { Switch } from "../ui/switch";
import {
  X, User, Mail, Phone, Receipt, DollarSign, StickyNote, Plus,
  Edit, Trash2, Send, ArrowRight, Clock, CheckCircle, AlertCircle, CalendarPlus, Eye
} from "lucide-react";
import { STATUS_COLORS, STATUS_LABELS, TIME_SLOTS } from "./shared";
import { sanitizeHtml } from "../../lib/safeMarkdown";
import VeniceSubPicker, { VENICE_LABEL, venicedbKey, veniceDisplayLabel } from "../VeniceSubPicker";
import { extractApiError } from "../../lib/apiError";

const PRIORITY_LOCATIONS = [VENICE_LABEL];

const TABS = [
  { id: "overview", label: "Overview", icon: User },
  { id: "orders", label: "Order History", icon: Receipt },
  { id: "notes", label: "Notes", icon: StickyNote },
  { id: "new_booking", label: "New Booking", icon: CalendarPlus },
  { id: "email", label: "Send Email", icon: Mail }
];

export default function CustomerDetailModal({ customerEmail, routes, onClose, onDataChange }) {
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/customers/${encodeURIComponent(customerEmail)}`);
      setDetail(res.data);
    } catch (_error) {
      toast.error("Failed to load customer details");
      onClose();
    } finally {
      setLoading(false);
    }
  }, [customerEmail, onClose]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const refresh = () => { fetchDetail(); onDataChange?.(); };

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/70 backdrop-blur-sm p-2 sm:p-4 overflow-y-auto" data-testid="customer-detail-modal">
      <div className="bg-[#0F0F0F] border border-white/10 rounded-sm w-full max-w-5xl my-4 max-h-[95vh] flex flex-col">
        {loading || !detail ? (
          <div className="flex items-center justify-center p-20">
            <div className="spinner" />
          </div>
        ) : (
          <>
            <Header detail={detail} onClose={onClose} onStatusChange={refresh} />
            <Tabs activeTab={activeTab} setActiveTab={setActiveTab} notesCount={detail.notes.length} ordersCount={detail.orders.length} />
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === "overview" && <OverviewPane detail={detail} />}
              {activeTab === "orders" && <OrdersPane orders={detail.orders} />}
              {activeTab === "notes" && <NotesPane email={detail.email} notes={detail.notes} onChange={refresh} />}
              {activeTab === "new_booking" && <NewBookingPane detail={detail} routes={routes} onCreated={() => { refresh(); setActiveTab("orders"); }} />}
              {activeTab === "email" && <EmailPane email={detail.email} name={detail.name} onSent={() => { refresh(); setActiveTab("notes"); }} />}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Header({ detail, onClose, onStatusChange }) {
  const [updating, setUpdating] = useState(false);
  const status = detail.user_status || "active";
  const isSuspended = status === "suspended";
  const isPending = status === "pending";

  const STATUS_BADGE = {
    active: { color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", label: "Active" },
    pending: { color: "bg-amber-500/20 text-amber-400 border-amber-500/30", label: "Pending Verification" },
    suspended: { color: "bg-red-500/20 text-red-400 border-red-500/30", label: "Suspended" }
  };
  const badge = STATUS_BADGE[status] || STATUS_BADGE.active;

  const updateStatus = async (newStatus) => {
    if (!detail.user_id) { toast.error("This customer has no account yet (guest)"); return; }
    if (!window.confirm(`Change status to "${newStatus}"?${newStatus === "suspended" ? "\n\nThis will revoke all active sessions and block future bookings/logins." : ""}`)) return;
    setUpdating(true);
    try {
      await axios.patch(`${API}/admin/users/${detail.user_id}/status`, { status: newStatus });
      toast.success(`Customer status updated to ${newStatus}`);
      onStatusChange();
    } catch (e) {
      toast.error(extractApiError(e, "Failed to update status"));
    } finally { setUpdating(false); }
  };

  return (
    <div className="flex items-start justify-between p-6 border-b border-white/5 flex-wrap gap-4">
      <div className="flex items-center gap-4">
        {detail.picture ? (
          <img src={detail.picture} alt={detail.name} className="w-14 h-14 rounded-full" />
        ) : (
          <div className="w-14 h-14 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
            <User className="w-7 h-7 text-[#C5A059]" />
          </div>
        )}
        <div>
          <h3 className="text-white text-xl font-semibold">{detail.name || "(no name)"}</h3>
          <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-white/50">
            <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {detail.email}</span>
            {detail.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {detail.phone}</span>}
            {detail.has_account ? (
              <span className={`px-2 py-0.5 rounded border ${badge.color}`} data-testid="customer-status-badge">{badge.label}</span>
            ) : (
              <span className="bg-white/10 text-white/40 px-2 py-0.5 rounded">Guest</span>
            )}
            {detail.auth_provider && <span className="text-white/30 text-[10px] uppercase">via {detail.auth_provider}</span>}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {detail.has_account && detail.user_id && (
          <>
            {isSuspended ? (
              <Button onClick={() => updateStatus("active")} disabled={updating}
                className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" data-testid="reactivate-btn">
                Reactivate
              </Button>
            ) : (
              <>
                {isPending && (
                  <Button onClick={() => updateStatus("active")} disabled={updating}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" data-testid="activate-btn">
                    Activate
                  </Button>
                )}
                <Button onClick={() => updateStatus("suspended")} disabled={updating}
                  variant="ghost" className="text-red-400 hover:text-red-300 hover:bg-red-500/10" size="sm" data-testid="suspend-btn">
                  Suspend
                </Button>
              </>
            )}
          </>
        )}
        <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="close-customer-detail">
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

function Tabs({ activeTab, setActiveTab, notesCount, ordersCount }) {
  return (
    <div className="flex border-b border-white/5 overflow-x-auto">
      {TABS.map(t => (
        <button key={t.id} onClick={() => setActiveTab(t.id)}
          className={`flex items-center gap-2 px-5 py-3 text-sm whitespace-nowrap transition-colors border-b-2 ${activeTab === t.id ? "border-[#C5A059] text-[#C5A059]" : "border-transparent text-white/50 hover:text-white"}`}
          data-testid={`customer-tab-${t.id}`}>
          <t.icon className="w-4 h-4" />
          {t.label}
          {t.id === "notes" && notesCount > 0 && <span className="bg-white/10 text-white/60 text-[10px] px-1.5 py-0.5 rounded">{notesCount}</span>}
          {t.id === "orders" && ordersCount > 0 && <span className="bg-white/10 text-white/60 text-[10px] px-1.5 py-0.5 rounded">{ordersCount}</span>}
        </button>
      ))}
    </div>
  );
}

function StatTile({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[#151515] border border-white/5 rounded-sm p-4">
      <Icon className={`w-5 h-5 mb-2 ${color || "text-white/30"}`} />
      <p className={`text-2xl font-bold font-mono ${color || "text-white"}`}>{value}</p>
      <p className="text-white/40 text-xs mt-1">{label}</p>
    </div>
  );
}

function OverviewPane({ detail }) {
  const { stats } = detail;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatTile icon={Receipt} label="Total Orders" value={stats.total_orders} />
        <StatTile icon={Clock} label="Pending" value={stats.pending_orders} color="text-amber-400" />
        <StatTile icon={CheckCircle} label="Confirmed" value={stats.confirmed_orders} color="text-emerald-400" />
        <StatTile icon={DollarSign} label="Revenue" value={`\u20AC${stats.total_revenue.toFixed(2)}`} color="text-[#C5A059]" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[#151515] border border-white/5 rounded-sm p-4">
          <p className="text-white/40 text-[10px] uppercase mb-2">Completed</p>
          <p className="text-emerald-400 text-xl font-mono">{stats.completed_orders}</p>
        </div>
        <div className="bg-[#151515] border border-white/5 rounded-sm p-4">
          <p className="text-white/40 text-[10px] uppercase mb-2">Cancelled</p>
          <p className="text-red-400 text-xl font-mono">{stats.cancelled_orders}</p>
        </div>
      </div>
      {detail.member_since && (
        <div className="bg-[#151515] border border-white/5 rounded-sm p-4 text-sm text-white/60">
          Member since: <span className="text-white font-mono">{detail.member_since.slice(0, 10)}</span>
        </div>
      )}
      {detail.notes.length > 0 && (
        <div>
          <p className="text-white/40 text-[10px] uppercase mb-2">Latest Note</p>
          <div className="bg-[#151515] border border-white/5 rounded-sm p-4">
            <p className="text-white font-semibold">{detail.notes[0].title}</p>
            <p className="text-white/60 text-sm mt-1 whitespace-pre-wrap">{detail.notes[0].content}</p>
            <p className="text-white/30 text-[10px] mt-2">{detail.notes[0].created_at?.slice(0, 10)} &middot; {detail.notes[0].author_name}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function OrdersPane({ orders }) {
  if (orders.length === 0) return <p className="text-white/40 text-center py-10">No orders yet</p>;
  return (
    <div className="space-y-3">
      {orders.map(o => (
        <div key={o.quote_id} className="bg-[#151515] border border-white/5 rounded-sm p-4 hover:border-white/10 transition-colors" data-testid={`customer-order-${o.quote_id}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-white text-sm">
                <span className="font-medium">{o.origin}</span>
                <ArrowRight className="w-3 h-3 text-[#C5A059]" />
                <span className="font-medium">{o.destination}</span>
                {o.is_round_trip && <span className="text-[9px] bg-blue-500/20 text-blue-400 px-1.5 py-0.5 rounded">RT</span>}
                {o.is_luxury && <span className="text-[9px] bg-[#C5A059]/20 text-[#C5A059] px-1.5 py-0.5 rounded">LUX</span>}
              </div>
              <p className="text-white/40 text-xs mt-1">
                {o.pickup_date} at {o.pickup_time}
                {o.is_round_trip && o.return_date && <span> &middot; Return {o.return_date} {o.return_time}</span>}
              </p>
              <p className="text-white/30 text-[10px] font-mono mt-1">ID: {o.quote_id}</p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-2 py-1 rounded text-[10px] uppercase border ${STATUS_COLORS[o.status]}`}>
                {STATUS_LABELS[o.status] || o.status}
              </span>
              <span className="text-[#C5A059] font-bold font-mono">{"\u20AC"}{o.total_price?.toFixed(2)}</span>
            </div>
          </div>
          {o.notes && <p className="text-white/40 text-xs mt-2 italic">Note: {o.notes}</p>}
        </div>
      ))}
    </div>
  );
}

function NotesPane({ email, notes, onChange }) {
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const startNew = () => { setEditing("new"); setTitle(""); setContent(""); };
  const startEdit = (n) => { setEditing(n.note_id); setTitle(n.title); setContent(n.content); };
  const cancel = () => { setEditing(null); setTitle(""); setContent(""); };

  const save = async () => {
    if (!title.trim() || !content.trim()) { toast.error("Title and content required"); return; }
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/customers/${encodeURIComponent(email)}/notes`, { title, content });
        toast.success("Note added");
      } else {
        await axios.put(`${API}/admin/customers/${encodeURIComponent(email)}/notes/${editing}`, { title, content });
        toast.success("Note updated");
      }
      cancel();
      onChange();
    } catch (error) {
      toast.error(extractApiError(error, "Failed to save note"));
    } finally { setSaving(false); }
  };

  const remove = async (noteId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await axios.delete(`${API}/admin/customers/${encodeURIComponent(email)}/notes/${noteId}`);
      toast.success("Note deleted");
      onChange();
    } catch (_error) { toast.error("Failed to delete note"); }
  };

  return (
    <div className="space-y-4">
      {!editing && (
        <Button onClick={startNew} className="btn-gold" data-testid="add-note-btn">
          <Plus className="w-4 h-4 mr-2" /> New Note
        </Button>
      )}

      {editing && (
        <div className="bg-[#151515] border border-[#C5A059]/30 rounded-sm p-4 space-y-3" data-testid="note-form">
          <Input placeholder="Note title (e.g. VIP customer, Payment pending...)" value={title} onChange={(e) => setTitle(e.target.value)} className="input-dark" data-testid="note-title" />
          <textarea
            placeholder="Note content..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className="w-full h-32 bg-[#1A1A1A] border border-[#333] rounded-sm p-3 text-white/80 text-sm resize-none focus:border-[#C5A059]/50 focus:outline-none"
            data-testid="note-content"
          />
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="bg-[#C5A059] hover:bg-[#B8903F] text-black" data-testid="save-note-btn">
              {saving ? "Saving..." : editing === "new" ? "Add Note" : "Update Note"}
            </Button>
            <Button variant="ghost" onClick={cancel} className="text-white/60">Cancel</Button>
          </div>
        </div>
      )}

      {notes.length === 0 && !editing && <p className="text-white/40 text-center py-10">No notes yet</p>}

      <div className="space-y-3">
        {notes.map(n => {
          const isEmailLog = n.is_email_log;
          const isExpanded = expanded === n.note_id;
          return (
            <div key={n.note_id} className={`bg-[#151515] border rounded-sm p-4 ${isEmailLog ? "border-violet-500/20" : "border-white/5"}`} data-testid={`note-${n.note_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {isEmailLog && <Mail className="w-3.5 h-3.5 text-violet-400 flex-shrink-0" />}
                    <h4 className="text-white font-medium">{n.title}</h4>
                  </div>
                  <p className="text-white/30 text-[10px] mt-1">
                    {n.created_at?.slice(0, 16).replace("T", " ")} &middot; by {n.author_name || "admin"}
                    {n.updated_at && n.updated_at !== n.created_at && " (edited)"}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  {isEmailLog && (
                    <button onClick={() => setExpanded(isExpanded ? null : n.note_id)} className="p-1.5 text-white/40 hover:text-violet-400">
                      <Eye className="w-4 h-4" />
                    </button>
                  )}
                  {!isEmailLog && (
                    <button onClick={() => startEdit(n)} className="p-1.5 text-white/40 hover:text-[#C5A059]" data-testid={`edit-note-${n.note_id}`}>
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => remove(n.note_id)} className="p-1.5 text-white/40 hover:text-red-400" data-testid={`delete-note-${n.note_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {(!isEmailLog || isExpanded) && (
                <div className="mt-3 text-white/70 text-sm whitespace-pre-wrap">
                  {isEmailLog ? (
                    <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-3 text-white/60 text-xs" dangerouslySetInnerHTML={{ __html: sanitizeHtml(n.content) }} />
                  ) : (
                    n.content
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NewBookingPane({ detail, routes, onCreated }) {
  const [form, setForm] = useState({
    user_name: detail.name || "",
    user_phone: detail.phone || "",
    origin: "",
    destination: "",
    route_id: "",
    pickup_date: "",
    pickup_time: "10:00",
    return_date: "",
    return_time: "10:00",
    passengers: 2,
    luggage: 2,
    is_luxury: false,
    is_round_trip: false,
    notes: "",
    status: "pending"
  });
  const [submitting, setSubmitting] = useState(false);

  const origins = useMemo(() => {
    const set = new Set();
    routes.forEach(r => {
      set.add(veniceDisplayLabel(r.origin));
      set.add(veniceDisplayLabel(r.destination));
    });
    const all = Array.from(set);
    const priority = PRIORITY_LOCATIONS.filter(p => all.includes(p));
    const rest = all.filter(l => !PRIORITY_LOCATIONS.includes(l)).sort();
    return { priority, rest };
  }, [routes]);

  const destinations = useMemo(() => {
    if (!form.origin) return { priority: [], rest: [] };
    const originKey = venicedbKey(form.origin);
    const map = new Map();
    routes.forEach(r => {
      if (r.origin === originKey) {
        const dispDest = veniceDisplayLabel(r.destination);
        if (!map.has(dispDest)) map.set(dispDest, { destination: dispDest, routeId: r.route_id });
      } else if (r.destination === originKey) {
        const dispDest = veniceDisplayLabel(r.origin);
        if (!map.has(dispDest)) map.set(dispDest, { destination: dispDest, routeId: r.route_id });
      }
    });
    const all = Array.from(map.values());
    const priority = PRIORITY_LOCATIONS.map(p => all.find(d => d.destination === p)).filter(Boolean);
    const rest = all.filter(d => !PRIORITY_LOCATIONS.includes(d.destination)).sort((a, b) => a.destination.localeCompare(b.destination));
    return { priority, rest };
  }, [routes, form.origin]);

  const resolveRouteId = (origin, destination) => {
    if (!origin || !destination || origin === VENICE_LABEL || destination === VENICE_LABEL) return "";
    const o = venicedbKey(origin);
    const d = venicedbKey(destination);
    const route = routes.find(r =>
      (r.origin === o && r.destination === d) ||
      (r.origin === d && r.destination === o)
    );
    return route?.route_id || "";
  };

  const handleOriginChange = (v) => setForm(p => ({ ...p, origin: v, destination: "", route_id: "" }));
  const handleDestinationChange = (v) => {
    setForm(p => ({ ...p, destination: v, route_id: resolveRouteId(p.origin, v) }));
  };
  const handleVeniceOriginPick = (full) => setForm(p => ({ ...p, origin: full, route_id: resolveRouteId(full, p.destination) }));
  const handleVeniceDestinationPick = (full) => setForm(p => ({ ...p, destination: full, route_id: resolveRouteId(p.origin, full) }));

  const handleSwap = () => {
    if (!form.origin || !form.destination) return;
    setForm(p => ({ ...p, origin: p.destination, destination: p.origin }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.origin || !form.destination || !form.pickup_date || !form.user_name || !form.user_phone) {
      toast.error("Please fill all required fields"); return;
    }
    if (form.origin === VENICE_LABEL || form.destination === VENICE_LABEL) {
      toast.error("Please choose Piazzale Roma or Airport for Venice"); return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/admin/customers/${encodeURIComponent(detail.email)}/bookings`, {
        ...form,
        return_date: form.is_round_trip && form.return_date ? form.return_date : null,
        return_time: form.is_round_trip ? form.return_time : null
      });
      toast.success("Booking created for customer");
      onCreated();
    } catch (error) {
      toast.error(extractApiError(error, "Failed to create booking"));
    } finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="new-booking-form">
      <div className="bg-[#C5A059]/5 border border-[#C5A059]/20 rounded-sm p-3 text-xs text-white/70">
        Creating booking for <span className="text-[#C5A059] font-semibold">{detail.email}</span>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-white/50 text-xs mb-1 block">Name *</label>
          <Input value={form.user_name} onChange={(e) => setForm(p => ({ ...p, user_name: e.target.value }))} className="input-dark" />
        </div>
        <div>
          <label className="text-white/50 text-xs mb-1 block">Phone *</label>
          <Input value={form.user_phone} onChange={(e) => setForm(p => ({ ...p, user_phone: e.target.value }))} className="input-dark" />
        </div>
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-start">
        <div>
          <label className="text-white/50 text-xs mb-1 block">From *</label>
          <Select value={veniceDisplayLabel(form.origin)} onValueChange={handleOriginChange}>
            <SelectTrigger className="input-dark w-full"><SelectValue placeholder="Origin" /></SelectTrigger>
            <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-72">
              {origins.priority.map(o => <SelectItem key={o} value={o} className="text-white hover:bg-[#333]">{o}</SelectItem>)}
              {origins.priority.length > 0 && origins.rest.length > 0 && <SelectSeparator className="my-1 bg-[#C5A059]/30" />}
              {origins.rest.map(o => <SelectItem key={o} value={o} className="text-white hover:bg-[#333]">{o}</SelectItem>)}
            </SelectContent>
          </Select>
          <VeniceSubPicker value={form.origin} onSelect={handleVeniceOriginPick} testIdPrefix="customer-origin" label="Pickup point" />
        </div>
        <button type="button" onClick={handleSwap} disabled={!form.origin || !form.destination}
          className="h-10 w-10 mt-6 bg-[#1F1F1F] border border-[#333] hover:border-[#C5A059]/40 disabled:opacity-30 text-[#C5A059] flex items-center justify-center rounded-sm transition-colors"
          title="Swap From/To" data-testid="customer-booking-swap">
          &#8644;
        </button>
        <div>
          <label className="text-white/50 text-xs mb-1 block">To *</label>
          <Select value={veniceDisplayLabel(form.destination)} onValueChange={handleDestinationChange} disabled={!form.origin || form.origin === VENICE_LABEL}>
            <SelectTrigger className="input-dark w-full"><SelectValue placeholder="Destination" /></SelectTrigger>
            <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-72">
              {destinations.priority.map(d => <SelectItem key={d.routeId} value={d.destination} className="text-white hover:bg-[#333]">{d.destination}</SelectItem>)}
              {destinations.priority.length > 0 && destinations.rest.length > 0 && <SelectSeparator className="my-1 bg-[#C5A059]/30" />}
              {destinations.rest.map(d => <SelectItem key={d.routeId} value={d.destination} className="text-white hover:bg-[#333]">{d.destination}</SelectItem>)}
            </SelectContent>
          </Select>
          <VeniceSubPicker value={form.destination} onSelect={handleVeniceDestinationPick} testIdPrefix="customer-destination" label="Drop-off point" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="text-white/50 text-xs mb-1 block">Pickup Date *</label>
          <Input type="date" value={form.pickup_date} onChange={(e) => setForm(p => ({ ...p, pickup_date: e.target.value }))} className="input-dark" />
        </div>
        <div>
          <label className="text-white/50 text-xs mb-1 block">Pickup Time *</label>
          <Select value={form.pickup_time} onValueChange={(v) => setForm(p => ({ ...p, pickup_time: v }))}>
            <SelectTrigger className="input-dark w-full"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-60">{TIME_SLOTS.map(t => <SelectItem key={t} value={t} className="text-white hover:bg-[#333]">{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-white/50 text-xs mb-1 block">Passengers</label>
          <Select value={form.passengers.toString()} onValueChange={(v) => setForm(p => ({ ...p, passengers: parseInt(v) }))}>
            <SelectTrigger className="input-dark w-full"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1F1F1F] border-[#333]">{[1, 2, 3, 4, 5, 6, 7].map(n => <SelectItem key={n} value={n.toString()} className="text-white hover:bg-[#333]">{n}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-6">
        <label className="flex items-center gap-2 text-white/50 text-sm">
          <Switch checked={form.is_luxury} onCheckedChange={(c) => setForm(p => ({ ...p, is_luxury: c }))} /> Luxury
        </label>
        <label className="flex items-center gap-2 text-white/50 text-sm">
          <Switch checked={form.is_round_trip} onCheckedChange={(c) => setForm(p => ({ ...p, is_round_trip: c }))} /> Round Trip
        </label>
      </div>

      {form.is_round_trip && (
        <div className="grid grid-cols-2 gap-4 p-3 bg-[#C5A059]/5 border border-[#C5A059]/20 rounded-sm">
          <div>
            <label className="text-white/50 text-xs mb-1 block">Return Date</label>
            <Input type="date" value={form.return_date} onChange={(e) => setForm(p => ({ ...p, return_date: e.target.value }))} className="input-dark" />
          </div>
          <div>
            <label className="text-white/50 text-xs mb-1 block">Return Time</label>
            <Select value={form.return_time} onValueChange={(v) => setForm(p => ({ ...p, return_time: v }))}>
              <SelectTrigger className="input-dark w-full"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#1F1F1F] border-[#333] max-h-60">{TIME_SLOTS.map(t => <SelectItem key={t} value={t} className="text-white hover:bg-[#333]">{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      )}

      <div>
        <label className="text-white/50 text-xs mb-1 block">Internal Notes</label>
        <Input value={form.notes} onChange={(e) => setForm(p => ({ ...p, notes: e.target.value }))} className="input-dark" placeholder="Optional notes" />
      </div>

      <div className="bg-[#151515] border border-white/5 rounded-sm p-4">
        <label className="text-white/50 text-xs mb-2 block flex items-center gap-2">
          <AlertCircle className="w-3.5 h-3.5 text-amber-400" /> Booking Status
        </label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            { v: "pending", label: "Pending (sends quote email)", active: "bg-amber-500/20 text-amber-400 border-amber-500/30" },
            { v: "awaiting_payment", label: "Awaiting Payment", active: "bg-orange-500/20 text-orange-400 border-orange-500/30" },
            { v: "confirmed", label: "Confirmed (no email)", active: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" },
            { v: "completed", label: "Completed", active: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
            { v: "cancelled", label: "Cancelled", active: "bg-red-500/20 text-red-400 border-red-500/30" }
          ].map(s => (
            <button key={s.v} type="button" onClick={() => setForm(p => ({ ...p, status: s.v }))}
              className={`text-xs p-2 rounded-sm border transition-colors ${form.status === s.v ? s.active : "bg-white/5 text-white/50 border-white/10 hover:bg-white/10"}`}
              data-testid={`status-option-${s.v}`}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      <Button type="submit" className="btn-gold w-full" disabled={submitting} data-testid="submit-customer-booking">
        {submitting ? "Creating..." : "Create Booking for Customer"}
      </Button>
    </form>
  );
}

function EmailPane({ email, name, onSent }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [sending, setSending] = useState(false);

  const previewHtml = useMemo(() => ({ __html: sanitizeHtml(body) }), [body]);

  const TEMPLATES = [
    {
      key: "follow_up",
      label: "Follow Up",
      subject: "Following up on your transfer request",
      body: `<p>Dear ${name || "Customer"},</p>
<p>We wanted to follow up regarding your recent transfer inquiry. Please let us know if you need any additional information or would like to proceed with your booking.</p>
<p>We are happy to assist you with any question.</p>
<p>Best regards,<br/><strong>TaxiNearMe Team</strong></p>`
    },
    {
      key: "thanks",
      label: "Thank You",
      subject: "Thank you for choosing TaxiNearMe",
      body: `<p>Dear ${name || "Customer"},</p>
<p>Thank you for travelling with TaxiNearMe. It was a pleasure serving you.</p>
<p>We would love to welcome you again on board. Use code <strong>WELCOMEBACK</strong> for 5% off your next ride.</p>
<p>Best regards,<br/><strong>TaxiNearMe Team</strong></p>`
    },
    {
      key: "reminder",
      label: "Payment Reminder",
      subject: "Gentle reminder - Your upcoming transfer",
      body: `<p>Dear ${name || "Customer"},</p>
<p>This is a friendly reminder about your upcoming transfer. Please contact us at your earliest convenience to confirm the details.</p>
<p>Best regards,<br/><strong>TaxiNearMe Team</strong></p>`
    }
  ];

  const applyTemplate = (t) => { setSubject(t.subject); setBody(t.body); };

  const send = async () => {
    if (!subject.trim() || !body.trim()) { toast.error("Subject and body are required"); return; }
    setSending(true);
    try {
      await axios.post(`${API}/admin/customers/${encodeURIComponent(email)}/email`, { subject, html_body: body });
      toast.success(`Email sent to ${email}`);
      setSubject(""); setBody(""); setShowPreview(false);
      onSent();
    } catch (error) {
      toast.error(extractApiError(error, "Failed to send email"));
    } finally { setSending(false); }
  };

  return (
    <div className="space-y-4" data-testid="email-form">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-white/40 text-xs">Templates:</span>
        {TEMPLATES.map(t => (
          <button key={t.key} onClick={() => applyTemplate(t)} type="button"
            className="text-xs px-3 py-1 bg-white/5 hover:bg-white/10 text-white/60 hover:text-white border border-white/10 rounded-sm"
            data-testid={`template-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div>
        <label className="text-white/50 text-xs mb-1 block">To</label>
        <Input value={email} disabled className="input-dark opacity-70" />
      </div>

      <div>
        <label className="text-white/50 text-xs mb-1 block">Subject *</label>
        <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="input-dark" placeholder="Email subject" data-testid="email-subject" />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-white/50 text-xs">Body (HTML supported) *</label>
          <button type="button" onClick={() => setShowPreview(!showPreview)} className="text-xs text-[#C5A059] hover:text-[#B8903F]" data-testid="toggle-preview">
            {showPreview ? "Edit" : "Preview"}
          </button>
        </div>
        {showPreview ? (
          <div className="border border-[#333] rounded-sm p-4 bg-white text-black min-h-[200px] text-sm" data-testid="email-preview" dangerouslySetInnerHTML={previewHtml} />
        ) : (
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            className="w-full h-48 bg-[#1A1A1A] border border-[#333] rounded-sm p-3 text-white/80 text-sm font-mono resize-none focus:border-[#C5A059]/50 focus:outline-none"
            placeholder="<p>Dear Customer,</p><p>...</p>"
            data-testid="email-body"
          />
        )}
        <p className="text-white/30 text-[10px] mt-1">Email will be branded with TaxiNearMe header/footer. Tags allowed: &lt;p&gt;, &lt;strong&gt;, &lt;a&gt;, &lt;br/&gt;, etc.</p>
      </div>

      <Button onClick={send} disabled={sending} className="btn-gold w-full" data-testid="send-email-btn">
        {sending ? "Sending..." : (<><Send className="w-4 h-4 mr-2" /> Send Email to {email}</>)}
      </Button>
    </div>
  );
}
